import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Unsubscribe,
} from '@firebase/firestore';
import { db } from './firebase';
import type { Place, PlaceStatus, Moderator, ModerationAction } from '../types';

const PLACES_COLLECTION = 'places';
const MODERATORS_COLLECTION = 'moderators';
const MODERATION_ACTIONS_COLLECTION = 'moderationActions';

/**
 * Check if a user is a moderator
 */
export async function isUserModerator(userId: string): Promise<boolean> {
  const docRef = doc(db, MODERATORS_COLLECTION, userId);
  const docSnap = await getDoc(docRef);
  return docSnap.exists();
}

/**
 * Get all moderators
 */
export async function getModerators(): Promise<Moderator[]> {
  const snapshot = await getDocs(collection(db, MODERATORS_COLLECTION));
  return snapshot.docs.map((doc) => ({
    ...doc.data(),
  })) as Moderator[];
}

/**
 * Get all pending places
 */
export async function getPendingPlaces(): Promise<Place[]> {
  const q = query(
    collection(db, PLACES_COLLECTION),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'asc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as Place[];
}

/**
 * Approve a place
 */
export async function approvePlace(
  placeId: string,
  moderatorId: string
): Promise<void> {
  // Check if user is moderator
  const isModerator = await isUserModerator(moderatorId);
  if (!isModerator) {
    throw new Error('Nur Moderatoren können Orte genehmigen');
  }

  const placeRef = doc(db, PLACES_COLLECTION, placeId);

  // Update place status
  await updateDoc(placeRef, {
    status: 'approved' as PlaceStatus,
    approvedBy: moderatorId,
    approvedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  // Log moderation action
  await addDoc(collection(db, MODERATION_ACTIONS_COLLECTION), {
    placeId,
    moderatorId,
    action: 'approve',
    timestamp: serverTimestamp(),
  });
}

/**
 * Reject a place
 */
export async function rejectPlace(
  placeId: string,
  moderatorId: string,
  reason: string
): Promise<void> {
  // Check if user is moderator
  const isModerator = await isUserModerator(moderatorId);
  if (!isModerator) {
    throw new Error('Nur Moderatoren können Orte ablehnen');
  }

  const placeRef = doc(db, PLACES_COLLECTION, placeId);

  // Update place status
  await updateDoc(placeRef, {
    status: 'rejected' as PlaceStatus,
    rejectionReason: reason,
    updatedAt: serverTimestamp(),
  });

  // Log moderation action
  await addDoc(collection(db, MODERATION_ACTIONS_COLLECTION), {
    placeId,
    moderatorId,
    action: 'reject',
    reason,
    timestamp: serverTimestamp(),
  });
}

/**
 * Get moderation history for a place
 */
export async function getModerationHistory(
  placeId: string
): Promise<ModerationAction[]> {
  const q = query(
    collection(db, MODERATION_ACTIONS_COLLECTION),
    where('placeId', '==', placeId),
    orderBy('timestamp', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({
    id: doc.id,
    ...doc.data(),
  })) as ModerationAction[];
}

/**
 * Subscribe to pending places (real-time) - for moderators
 */
export function subscribeToPendingPlaces(
  callback: (places: Place[]) => void
): Unsubscribe {
  const q = query(
    collection(db, PLACES_COLLECTION),
    where('status', '==', 'pending'),
    orderBy('createdAt', 'asc')
  );

  return onSnapshot(q, (snapshot) => {
    const places = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Place[];
    callback(places);
  });
}

/**
 * Get count of pending places
 */
export async function getPendingPlacesCount(): Promise<number> {
  const q = query(
    collection(db, PLACES_COLLECTION),
    where('status', '==', 'pending')
  );

  const snapshot = await getDocs(q);
  return snapshot.size;
}

/**
 * Resubmit a rejected place for review
 */
export async function resubmitPlace(
  placeId: string,
  userId: string
): Promise<void> {
  const placeRef = doc(db, PLACES_COLLECTION, placeId);
  const placeSnap = await getDoc(placeRef);

  if (!placeSnap.exists()) {
    throw new Error('Ort nicht gefunden');
  }

  const place = placeSnap.data() as Place;

  // Only the creator can resubmit
  if (place.createdBy !== userId) {
    throw new Error('Nur der Ersteller kann den Ort erneut einreichen');
  }

  // Only rejected places can be resubmitted
  if (place.status !== 'rejected') {
    throw new Error('Nur abgelehnte Orte können erneut eingereicht werden');
  }

  await updateDoc(placeRef, {
    status: 'pending' as PlaceStatus,
    rejectionReason: null,
    updatedAt: serverTimestamp(),
  });
}
